import * as React from "react";
import { useEffect, useState } from "react";
import "./PatientTagSetter.scss";
import { PatientInformation, Tag } from "../commons/model/patient/PatientInfo";
import Label, { LabelStyle } from "../label/Label";
import SelectableOption from "../commons/model/SelectableOption";
import sfetch from "../commons/utils/super-fetch";
import OptionsSelector from "../commons/components/OptionsSelector";

export interface TagModalInfo {
  displayModal: boolean;
  patient: PatientInformation;
  currentFilter: string;
}

interface PatientTagSetterProps {
  patientTags: Tag[];
  availableTags: Tag[];
  editUrl?: string;
  onChange?: (tag: string, selected: boolean) => any;
  createTagUrl: string;
  onTagAdd: (any) => void;
  patient: PatientInformation;
  displayModalHandler: boolean;
  addButton?: any;
}

export default function PatientTagSetter({
  patientTags,
  availableTags,
  editUrl,
  onChange,
  onTagAdd,
  patient,
  displayModalHandler,
  addButton,
}: PatientTagSetterProps) {
  const onTagChange = (tagUid, selected) => {
    sfetch(editUrl, {
      method: selected ? "delete" : "put",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tagUid }),
    }).then(() => onChange?.(tagUid, selected));
  };

  useEffect(() => {
    setCurrentFilter("");
  }, [availableTags]);

  const [filteredTags, setFilteredTags] = useState<Tag[]>(availableTags);
  const [currentFilter, setCurrentFilter] = useState<string>("");

  const [displayModal, setDisplayModal] = useState<boolean>(displayModalHandler);

  const addLabelButton = (
    <button
      id={"add-label-button"}
      data-test={"add-label-button"}
      className="label option"
      onClick={(e) => {
        onTagAdd({
          displayModal: !displayModal,
          patient,
          currentFilter,
        } as TagModalInfo);
        e.stopPropagation();
      }}
    >
      <i className="ic_plus"></i>
      <span>{i18n["Page.Settings.Tags.Modal.Form.Title.Add"]}</span>
    </button>
  );

  useEffect(() => {
    setDisplayModal(displayModalHandler);
  }, [displayModalHandler]);

  useEffect(() => {
    setFilteredTags(
      availableTags.filter((t) => {
        return t.value.toUpperCase().includes(currentFilter.toUpperCase());
      })
    );
  }, [currentFilter, availableTags]);

  const buildTags = () => (
    <div className="label-container">
      {patientTags.map((it) => (
        <Label
          label={it.value}
          key={it.uid}
          value={it.uid}
          style={LabelStyle.PRIMARY}
          customStyle={it.color ? { backgroundColor: it.color, borderColor: it.color } : {}}
          removeAction={(tag) =>
            onTagChange(tag, patientTags.find((value) => value.uid === it.uid) != undefined)
          }
        />
      ))}
    </div>
  );

  const buildEditableContent = () => (
    <div className="h-flex no-wrap align-center space-between group">
      {buildTags()}

      <div className="label-container">
        <Label
          style={LabelStyle.SECONDARY}
          label={
            <OptionsSelector
              onChange={(_tagOptions, tagOption) =>
                onTagChange(tagOption.value, tagOption.selected)
              }
              options={filteredTags.map(
                (tag: Tag) =>
                  new SelectableOption(
                    tag.uid,
                    tag.value,
                    patientTags.find((value) => value.uid == tag.uid) != undefined
                  )
              )}
              toggleView={() => (
                <div className="h-flex align-center primary-colored">
                  <>
                    <span className="no-width">&nbsp;</span>
                    {addButton && addButton}
                    {!addButton && <i className="ic_plus" />}
                  </>
                </div>
              )}
              onCurrentFilterChange={setCurrentFilter}
              addLabelButton={addLabelButton}
            />
          }
        />
      </div>
    </div>
  );

  const buildNonEditableContent = () => (
    <div className="label-container">
      {patientTags.map((it) => (
        <Label
          label={it.value}
          key={it.uid}
          style={LabelStyle.PRIMARY}
          customStyle={it.color ? { backgroundColor: it.color, borderColor: it.color } : {}}
        />
      ))}
    </div>
  );

  let content;

  if (editUrl) {
    content = buildEditableContent();
  } else {
    content = buildNonEditableContent();
  }

  return <div className="patient-tag-setter">{content}</div>;
}
